<template>
  <div class="note-category-paginated-overview flex-col gap-y-sm" v-bind:class="{loaded: !loading}">
    <notes-filter-component
        @input="(newFilter) => setFilter(newFilter)"
    ></notes-filter-component>

    <dd-loading-indicator
        v-if="loading"
        message="Notities zijn aan het laden..." :loading="loading">
    </dd-loading-indicator>

    <div v-if="!hasItems && !loading" style="padding: 8px;">
      <p v-if="filterActive">
        Er zijn geen notities die voldoen aan de huidige filtering.
      </p>
      <p v-else-if="error">
        Er ging iets mis. Het is niet gelukt de notities te laden.
      </p>
      <p v-else>
        Er zijn nog geen notities voor deze categorie.
      </p>
    </div>

    <template v-if="!loading && hasItems">
      <dd-main-table
          :loading="loading"
          :initial-load-page-object="loadPageObject"
          @load-page="(pageObject) => onLoadPage(pageObject)"
          :rounded="true"
          :has-select="false"
          :headers="tableHeaders"
          :rows="notes"
          :skeleton-row-count="notes.length"
          :paginator="pagination">
        <template #actions="{ row }">
          <div class="flex-row gap-sm">
            <dd-button class="square-button" @click="emitViewNote(row)" :small="true"
                       title="Bekijken" data-dusk="view-note">
              <template #icon>
                <EyeIcon></EyeIcon>
              </template>
            </dd-button>
            <dd-button class="square-button" @click="emitEditNoteAttachable(row)" :small="true"
                       title="Bewerken" data-dusk="edit-note">
              <template #icon>
                <PencilIcon></PencilIcon>
              </template>
            </dd-button>
            <dd-button type="danger" class="square-button" @click="onDelete(row)" :small="true"
                       title="Verwijderen" data-dusk="delete-note">
              <template #icon>
                <TrashIcon></TrashIcon>
              </template>
            </dd-button>
          </div>
        </template>
      </dd-main-table>
    </template>
  </div>
</template>

<script>
import noteApi from '~/api/note'
import AttachableNoteTableRowComponent from '../../../modules/note/components/AttachableRowComponent.vue';
import { mapState, mapMutations, mapActions } from 'vuex';
import moment from 'moment';
import DateFormatEnum from '../../../enums/DateFormatEnum';
import { Skeleton } from 'vue-loading-skeleton'
import OwnerCell from './cell_types/OwnerCell.vue';
import CreatedAtCell from "./cell_types/CreatedAtCell.vue";
import TitleCell from './cell_types/TitleCell.vue';
import UserAvatarCell from '~/new_design_components/tables/cell_types/UserAvatarCell.vue';
import NotesFilterComponent from "./NotesFilterComponent.vue";
export default {
  name: 'NoteCategoryPaginatedOverviewComponent',
  emits: ['edit', 'view'],
  props: {
    categoryId: {
      type: [Number, String],
      required: false,
    },
    customerId: {
      type: [Number, String],
      default: null,
    },
    instance: {
      type: Object,
      default: null
    },
    publishDocuments: {
      type: Boolean,
      default: false,
    },
    showFilterOptions: {
      type: Boolean,
      default: true,
    },
    count: {
      type: Number,
      default: 0,
    }
  },
  components: {
    AttachableNoteTableRowComponent,
    Skeleton,
    OwnerCell,
    CreatedAtCell,
    NotesFilterComponent,
    TitleCell,
  },
  data () {
    return {
      showFilterDropdown: false,
      loadPageObject: {
        page: 1,
        perPage: 15,
        orderBy: 'created_at',
        order: 'desc',
      },
      reloadTimeout: null,
      notes: [],
      currentRequestData: null,
      loading: true,
      includes: ['user'],
      error: false,
      filter: null,
      context: null,
      tableHeaders: [
        {
          title: "Onderwerp",
          name: 'title',
          key: (row) => row.title,
          typeComponent: TitleCell,
          width: 50,
        },
        {
          title: 'Aangemaakt op',
          name: 'created_at',
          key: (row) => moment(row.created_at).format(DateFormatEnum.DISPLAY_DATE),
          width: 50,
        },
        {
          title: "Aangemaakt door",
          name: 'user_id',
          key: (row) => row.user.data,
          typeComponent: UserAvatarCell,
          width: 50,
        },
      ],
    }
  },
  created () {
    this.setContext()
  },
  computed: {
    ...mapState(`$_notes_drawer`, {
      reloadNotes: 'reloadNotes',
    }),
    loadingCount () {
      return this.count < this.perPage ? this.count : this.perPage
    },
    pagination () {
      return this.currentRequestData?.meta?.pagination;
    },
    hasItems () {
      return this.notes.length > 0;
    },
    filterActive () {
      if (Object.entries(this.filter)) {
        for (const [key, value] of Object.entries(this.filter)) {
          if (value !== null && value.length > 0) return true
        }
      }
      return false
    }
  },
  watch: {
    reloadNotes() {
      if (this.reloadNotes) {
        this.loadPage();
      }
      this.setReloadNotes(false);
    },
  },
  methods: {
    ...mapActions(`$_customer_page`, {
      getNotesCount: 'getNotesCount',
    }),
    ...mapMutations(`$_notes_drawer`, {
      setReloadNotes: 'SET_RELOAD_NOTES',
    }),
    setFilter(newFilter) {
      this.filter = newFilter;
      this.loadPage();
    },
    emitEditNoteAttachable(row) {
      this.$emit('edit', row.id);
    },
    emitViewNote(row) {
      this.$emit('view', row.id);
    },
    onLoadPage(pageObject) {
      this.loadPageObject = pageObject;
      this.loadPage();
    },
    setContext () {
      if (this.instance !== null) {
        this.context = 'instance'
      } else {
        this.context = 'family'
      }
    },
    loadPage () {
      this.loading = true

      if (this.context === 'instance') {
        noteApi.paginateByInstance(
            this.instance,
            this.loadPageObject?.page,
            this.loadPageObject?.perPage,
            this.loadPageObject?.orderBy,
            this.loadPageObject?.order,
            this.includes,
            this.filter
        ).then((response) => {
          this.onSuccessResponse(response);
        }).catch((error) => {this.onErrorResponse();
        }).finally(() => {
          this.loading = false
        })
      } else {
        noteApi.paginateByFamilyCategory(
            this.customerId,
            this.categoryId,
            this.loadPageObject?.page,
            this.loadPageObject.perPage,
            this.loadPageObject.orderBy,
            this.loadPageObject.order,
            this.includes,
            this.filter
        ).then((response) => {
          this.onSuccessResponse(response);
        }).catch((error) => {
          this.onErrorResponse();
        }).finally(() => {
          this.loading = false
        })
      }
    },
    onSuccessResponse(response) {
      this.notes = response.data
      this.currentRequestData = response
    },
    onErrorResponse() {
      this.error = true;
      this.$notification['error']({
        message: 'Er ging iets mis',
        description: 'Het is niet gelukt de notities te laden.',
      })
    },
    reloadPage (page = null) {
      if (page === null) {
        this.loadPageObject.page = this.loadPageObject.currentPage
      } else {
        this.loadPageObject.page = page
      }

      if (this.reloadTimeout !== null) {
        clearTimeout(this.reloadTimeout)
      }

      this.reloadTimeout = setTimeout(() => {
        this.loadPage()
      }, 200)
    },
    getFilteredAttachables (params) {
      this.filter = params
      this.loadPage()
    },
    onDelete (note) {
      let $self = this

      this.$confirm({
        title: 'Weet je zeker dat je deze notitie wil verwijderen?',
        content: 'De geselecteerde notitie zal worden verwijderd. Deze actie is niet terug te draaien',
        okText: 'Verwijderen',
        okType: 'danger',
        cancelText: 'Annuleren',
        onOk () {
          noteApi.delete(note)
              .then(() => {
                this.$notification['success']({
                  message: 'Success',
                  description: 'De notitie is verwijderd',
                })
                this.getNotesCount();
              })
              .catch(() => {
                this.$notification['error']({
                  message: 'Er ging iets mis',
                  description: 'Het is niet gelukt de notitie te verwijderen',
                })
              })
              .finally(async () => {
                await $self.reloadPage()
                this.loading = false
              })
        },
        onCancel () {
        },
      })
    },
  },
}
</script>

<style scoped>
.note-category-paginated-overview {
  min-height: 50px;
}

table th.compact, table td.compact {
  white-space: nowrap;
  width: 1px !important;
}

table > tbody.loading {
  opacity: 0.5;
}

table {
  margin-top: 0 !important;
  margin-bottom: 0 !important;
}

.pagination-buttons {
  display: inline-block;
  vertical-align: text-top;
  margin: 0;
  padding: 8px;
}

.skeleton-paragraph {
  width: 100%;
  height: 1.5em;
  display: inline-block;
}

.skeleton-user-badge {
  width: 93px;
  height: 32px;
  display: inline-block;
}

.skeleton-icon {
  width: 30px;
  height: 23px;
  display: inline-block;
  border-radius: 4px;
}

.skeleton-button {
  width: 46px;
  height: 32px;
  display: inline-block;
  border-radius: 4px;
}
</style>
